import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
    ViewChild,
    TemplateRef,
    OnInit,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
    FiSelectSearchValidationFn,
} from '../fi-select-search';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-select-search-chips-trigger',
    templateUrl: './fi-select-search-chips-trigger.component.html',
    styleUrls: ['./fi-select-search-chips-trigger.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-select-search-chips-trigger' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiSelectSearchChipsTriggerComponent }],
    standalone: false,
})
export class FiSelectSearchChipsTriggerComponent<T extends { Id: number }, TRes = number>
    extends FiComponentsContainer
    implements OnInit, OnChanges
{
    @Input() availableItems: T[] = [];
    @Input() isRequired: boolean = false;
    @Input() isDisabled: boolean = false;
    @Input() displayValue: string = 'FullName';
    @Input() filterBy: string = 'FullName';
    @Input() matOptionValueProperty: string = 'Id';
    @Input() labelText: string;
    @Input() hintText: string;
    @Input() errorText: string;
    @Input() isProfileOption: boolean = false;
    @Input() includeDisabledToSearchResult: boolean = false;
    @Input() isCheckboxOptionsHidden: boolean = false;
    @Input() isOptionDisabledFn: FiDropdownItemDisabledFn<T> = () => false;
    @Input() getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<T> = () => undefined;
    @Input() getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<T> = () => undefined;
    @Input() validationFn: FiSelectSearchValidationFn<T>;
    @Input() initialSelectedItems: T[];

    @Input() selectedItemIds: TRes[] = [];
    @Output() selectedItemIdsChange = new EventEmitter<TRes[]>();

    private availableItemIds: number[];

    @ViewChild('profileMatOptionTemplate', { static: true }) profileMatOptionTemplate: TemplateRef<any>;
    protected items: T[] = [];

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.setItemsAvailableForSelect();
        this.setSelectedDisplayItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.availableItems && !changes.availableItems.firstChange) {
            this.setItemsAvailableForSelect();
        }

        if (changes.selectedItemIds && !changes.selectedItemIds.firstChange) {
            this.setSelectedDisplayItems();
        }
    }

    private setItemsAvailableForSelect() {
        this.availableItemIds = this.availableItems.map((availableItem) => availableItem.Id);
        const itemsWithNoAccess = isDefined(this.initialSelectedItems)
            ? this.initialSelectedItems.filter((item) => !this.availableItemIds.includes(item.Id))
            : [];
        this.items = [...this.availableItems, ...itemsWithNoAccess];
    }

    protected isOptionHidden: FiDropdownItemDisabledFn<T> = (item: T, allItems: T[]) => {
        return !this.availableItemIds.includes(item.Id);
    };

    protected getPanelClass(): string {
        let panelClass: string = 'fi-select-panel-standard';
        if (this.isCheckboxOptionsHidden) {
            panelClass += ' checkbox-option-hidden';
        }
        if (this.isProfileOption) {
            panelClass += ' fi-select-panel-profile-option';
        }
        return panelClass;
    }

    protected onSelectedItemsChange(): void {
        this.setSelectedDisplayItems();
        this.selectedItemIdsChange.emit(this.selectedItemIds);
    }

    protected removeSelectedItem($event: MouseEvent, selectedDisplayItemValue: TRes): void {
        $event.stopPropagation();
        this.selectedItemIds = this.selectedItemIds.filter((item) => item !== selectedDisplayItemValue);
        this.onSelectedItemsChange();
    }

    private _selectedDisplayItems$ = new BehaviorSubject<T[]>([]);
    private setSelectedDisplayItems(): void {
        const newSelectedDisplayItems = this.selectedItemIds
            .map((itemValue) => this.items.find((item) => item[this.matOptionValueProperty] === itemValue))
            .filter((selectedItem) => isDefined(selectedItem));
        this._selectedDisplayItems$.next(newSelectedDisplayItems);
    }
    protected get selectedDisplayItems$(): Observable<T[]> {
        return this._selectedDisplayItems$.asObservable();
    }

    protected readonly FiProfileSizes = FiProfileSizes;
}
